import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useManualEmailCampaign } from '../../hooks/useManualEmailCampaign';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';
import { useAuth } from '../../AuthContext';

interface ConfirmDialogState {
  isOpen: boolean;
  signatureId: number | null;
  signerName: string;
}

export const ManualEmailDraft: React.FC = () => {
  const navigate = useNavigate();
  const { currentUserAccountUniqueId, viewingAllAccounts } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [includeNoEmail, setIncludeNoEmail] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    signatureId: null,
    signerName: ''
  });
  const copy = useLocalizedCopy();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const {
    searchSignatures,
    fetchRecentSignatures,
    createManualCampaign,
    clearResults,
    searchResults,
    searchError,
    isSearching,
    isCreating
  } = useManualEmailCampaign();

  // Fetch recent signatures on mount
  useEffect(() => {
    fetchRecentSignatures(includeNoEmail);
  }, [includeNoEmail]);

  // Maintain focus on search input after results update
  useEffect(() => {
    // Store current cursor position
    const input = searchInputRef.current;
    if (input && document.activeElement === input) {
      const cursorPosition = input.selectionStart;
      // Use requestAnimationFrame to wait for DOM update
      requestAnimationFrame(() => {
        input.focus();
        // Restore cursor position
        if (cursorPosition !== null) {
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    }
  }, [searchResults]);

  // Handle click outside for popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowOptionsPopover(false);
      }
    };

    if (showOptionsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsPopover]);

  // Manual debounce implementation
  const debouncedSearch = useCallback((term: string, noEmail: boolean) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      if (term.length === 0) {
        // If search is cleared, fetch recent signatures
        fetchRecentSignatures(noEmail);
      } else {
        // Always search regardless of length
        searchSignatures(term, noEmail);
      }
    }, 300);
  }, [searchSignatures, fetchRecentSignatures]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value, includeNoEmail);
  };

  const handleIncludeNoEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIncludeNoEmail(checked);
    // Always update results when toggling the checkbox
    if (searchTerm.length > 0) {
      searchSignatures(searchTerm, checked);
    } else {
      fetchRecentSignatures(checked);
    }
  };

  const handleCreateClick = (signatureId: number, signerName: string, hasCampaign: boolean) => {
    if (hasCampaign) {
      // Show confirmation dialog
      setConfirmDialog({
        isOpen: true,
        signatureId,
        signerName
      });
    } else {
      // Create directly
      handleCreateCampaign(signatureId);
    }
  };

  const handleCreateCampaign = async (signatureId: number) => {
    setSuccessMessage('');
    setErrorMessage('');
    setConfirmDialog({ isOpen: false, signatureId: null, signerName: '' });
    
    const result = await createManualCampaign(signatureId);
    if (result.success) {
      // Instead of showing success and redirecting, navigate to review with campaign ID
      if (result.campaignId) {
        console.log('[ManualEmailDraft] Navigating to review with campaign ID:', result.campaignId);
        // Navigate to review queue with the campaign ID to open modal
        navigate('/follow-up-messages/review', { 
          state: { 
            openCampaignId: result.campaignId,
            isGenerating: true 
          } 
        });
      } else {
        // Fallback to old behavior if no campaign ID
        setSuccessMessage(result.message || 'Message created successfully - redirecting to review queue...');
        setTimeout(() => {
          navigate('/follow-up-messages/review');
        }, 1500);
      }
    } else {
      setErrorMessage(result.error || 'Failed to create message');
      // Clear error message after 5 seconds
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  // Simple date formatting function
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  return (
    <div className="container-fixed">
      <div className="bg-white shadow rounded-lg">
        {/* Compact Header with Search */}
        <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                ref={searchInputRef}
                id="signature-search"
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name or view recent signatures..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                disabled={isSearching}
              />
              <i className="ki-filled ki-magnifier absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
          
          {/* Options Button */}
          <div className="relative" ref={popoverRef}>
            <button
              onClick={() => setShowOptionsPopover(!showOptionsPopover)}
              className="btn btn-sm btn-light"
              title="Options"
            >
              <i className="ki-filled ki-setting-2"></i>
              Options
            </button>
            
            {/* Options Popover */}
            {showOptionsPopover && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <div className="p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Search Options</h4>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeNoEmail}
                      onChange={handleIncludeNoEmailChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Include signatures without email addresses</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    When enabled, signatures without email addresses will be shown but cannot create campaigns.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}
        
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{errorMessage}</span>
          </div>
        )}

        {/* Loading State */}
        {isSearching && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-gray-600">Searching...</p>
          </div>
        )}

        {/* Search Error */}
        {!isSearching && searchError && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <i className="ki-filled ki-information-5 text-yellow-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm">{searchError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {!isSearching && !searchError && searchResults.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {(viewingAllAccounts || !currentUserAccountUniqueId) && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {searchResults.map((signature) => (
                <tr key={signature.id}>
                  {(viewingAllAccounts || !currentUserAccountUniqueId) && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-medium">
                      {signature.account_name || 'Unknown Account'}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {signature.signer_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {signature.signer_email || <span className="text-gray-400">(No email)</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {signature.service_unique_identifier ? (
                      <Link 
                        to={`/services/${signature.service_unique_identifier}`}
                        className="text-primary hover:text-primary-dark underline"
                      >
                        {signature.service_name}
                      </Link>
                    ) : (
                      signature.service_name
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(signature.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {signature.signer_email ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCreateClick(signature.id, signature.signer_name, signature.has_email_campaign)}
                          disabled={isCreating}
                          className="text-blue-600 hover:text-blue-900 font-medium disabled:opacity-50"
                        >
                          Create Draft
                        </button>
                        {signature.has_email_campaign && (
                          <span className="text-yellow-600 text-xs">(Existing campaign)</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">No email</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

        {/* No Results */}
        {!isSearching && !searchError && searchTerm.length > 0 && searchResults.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No signatures found matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <>
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40" onClick={() => setConfirmDialog({ isOpen: false, signatureId: null, signerName: '' })}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0">
                  <i className="ki-filled ki-information-circle text-yellow-400 text-2xl"></i>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Campaign Already Exists
                  </h3>
                  <div className="mt-2 text-sm text-gray-500">
                    <p>An email campaign has already been created for <strong>{confirmDialog.signerName}</strong>.</p>
                    <p className="mt-2">Do you want to create another campaign for this signature?</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setConfirmDialog({ isOpen: false, signatureId: null, signerName: '' })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDialog.signatureId && handleCreateCampaign(confirmDialog.signatureId)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create Another
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};